import React, { useState } from 'react';
import { Card } from './components/UI';
import { useTranslation } from './i18n';

// API URLs - zelfde als in App.jsx
const COMPANY_API = process.env.REACT_APP_COMPANY_API || 'http://127.0.0.1:8001';
const CANDIDATE_API = process.env.REACT_APP_CANDIDATE_API || 'http://127.0.0.1:8002';

export default function AccessCodeScreen({ userType, onSuccess, onBack }) {
  const { t } = useTranslation();
  const [code, setCode] = useState('');
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [requestBusy, setRequestBusy] = useState(false);
  const [requestSent, setRequestSent] = useState(false);
  const [requestError, setRequestError] = useState(null);
  const [requestForm, setRequestForm] = useState({
    name: '',
    email: '',
    company_name: '',
    phone: '',
    message: ''
  });

  // Kies de juiste API URL
  const apiBase = userType === 'candidate' ? CANDIDATE_API : COMPANY_API;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setBusy(true);

    try {
      const response = await fetch(`${apiBase}/api/validate-access-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: code.trim() })
      });

      const data = await response.json();

      if (response.ok && data.ok) {
        onSuccess();
      } else {
        setError(data.detail || t('access_code.invalid_code'));
      }
    } catch (err) {
      setError(t('access_code.server_unreachable'));
    }
    
    setBusy(false);
  };

  const isCandidate = userType === 'candidate';
  const title = isCandidate ? t('access_code.talent_portal') : t('access_code.business_portal');
  const accessCodeInputClass = 'w-full rounded border-2 border-[#151827] bg-[#fffdf8] p-3 transition-colors focus:border-[#3f7f65] focus:bg-white focus:outline-none';
  const companyInputClass = `${accessCodeInputClass} text-sm font-semibold text-[#151827] placeholder:text-[#151827]/45`;
  const primaryButtonClass = 'w-full rounded-xl bg-[#3f7f65] py-3 font-black text-white transition-all hover:translate-x-0.5 hover:translate-y-0.5';
  const companySecondaryButtonClass = 'w-full rounded-xl border-2 border-[#151827] bg-white py-3 text-sm font-black uppercase tracking-wide text-[#151827] transition-all hover:translate-x-0.5 hover:translate-y-0.5';

  const handleAccessCodeRequest = async (e) => {
    e.preventDefault();
    setRequestError(null);

    if (!requestForm.name.trim() || !requestForm.email.trim() || !requestForm.company_name.trim()) {
      setRequestError(t('access_code.request_required_fields'));
      return;
    }

    setRequestBusy(true);
    try {
      const response = await fetch(`${COMPANY_API}/api/company/access-code-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: requestForm.name.trim(),
          email: requestForm.email.trim(),
          company_name: requestForm.company_name.trim(),
          phone: requestForm.phone.trim(),
          message: requestForm.message.trim()
        })
      });

      const data = await response.json();

      if (response.ok && data.ok) {
        setRequestSent(true);
      } else {
        setRequestError(data.detail || t('access_code.request_failed'));
      }
    } catch (err) {
      setRequestError(t('access_code.request_submit_error'));
    }

    setRequestBusy(false);
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#f6f1e8] p-4 text-[#151827]">
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.10]"
        style={{
          backgroundImage: 'radial-gradient(#151827 1px, transparent 1px)',
          backgroundSize: '18px 18px',
        }}
      />
      <div className="pointer-events-none absolute -left-24 -top-24 h-80 w-80 rounded-full bg-[#3f7f65] opacity-25 blur-3xl" />
      <div className="pointer-events-none absolute -right-24 top-8 h-[28rem] w-[28rem] rounded-full bg-[#713d8d] opacity-25 blur-3xl" />

      <div className="relative z-10 mx-auto w-full max-w-6xl py-4 md:py-10">
        <Card
          className="mx-auto w-full max-w-2xl rounded-[1.6rem] border-4 border-[#151827] bg-white p-6 md:p-8"
          style={{ boxShadow: '14px 14px 0 #151827' }}
        >
          <div className="mb-8 text-center">
            <div className="mb-4 inline-block bg-[#3f7f65] px-4 py-2 text-xs font-black uppercase tracking-wider text-white" style={{ clipPath: 'polygon(0 14%, 96% 0, 100% 84%, 8% 100%)' }}>
              {t('auth.brand_tagline')}
            </div>
            <h1 className="mb-2 text-2xl font-black text-[#3f7f65]">{title}</h1>
            <p className="text-xs font-black uppercase tracking-[0.2em] text-[#151827]/65">
              {isCandidate ? 'Betaaldsolliciteren.nl' : 'werepresenttalent.nl'}
            </p>
            <p className="text-sm text-gray-500">{t('access_code.enter_code_prompt')}</p>
            <p className="mt-1 text-sm text-gray-500">{t('access_code.code_needed_explainer')}</p>
          </div>

          {error && (
            <div className="mb-6 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-600">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="mb-2 block text-xs font-bold uppercase text-gray-700">{t('access_code.label')}</label>
              <input
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder={t('access_code.placeholder')}
                className={`${accessCodeInputClass} text-center text-lg font-mono uppercase tracking-widest`}
                autoFocus
                required
              />
            </div>

            <button
              type="submit"
              disabled={busy || !code.trim()}
              className={`w-full text-white transition-all ${
                busy || !code.trim()
                  ? 'cursor-not-allowed rounded-lg bg-gray-300'
                  : primaryButtonClass
              }`}
              style={!(busy || !code.trim()) ? { boxShadow: '8px 8px 0 #151827' } : undefined}
            >
              {busy ? t('access_code.checking') : t('access_code.continue')}
            </button>
          </form>

          <div className="mt-6 text-center">
            <button
              onClick={onBack}
              className="text-sm font-semibold text-[#151827]/70 transition-colors hover:text-[#151827]"
            >
              {t('access_code.back_to_menu')}
            </button>
          </div>

          {!isCandidate && (
            <div className="mt-8 border-t border-gray-200 pt-6">
              <p className="mb-4 text-xs font-black uppercase tracking-[0.18em] text-[#151827]/65">{t('access_code.no_code_yet')}</p>
              <p className="text-sm text-[#2a2f40]">{t('access_code.request_intro')}</p>

              {!showRequestForm && !requestSent && (
                <button
                  type="button"
                  onClick={() => setShowRequestForm(true)}
                  className="mt-4 w-full border-4 border-[#151827] bg-white px-4 py-3 text-left text-sm font-black uppercase tracking-[0.12em] text-[#151827] transition-colors hover:bg-[#f3f3f3]"
                >
                  {t('access_code.request_button')}
                </button>
              )}

              {requestSent && (
                <div className="mt-4 border-4 border-[#151827] bg-[#d9eadf] px-4 py-4 text-sm font-semibold text-[#151827]">
                  {t('access_code.request_success')}
                </div>
              )}

              {showRequestForm && !requestSent && (
                <form onSubmit={handleAccessCodeRequest} className="mt-4 space-y-3">
                  {requestError && (
                    <div className="border-4 border-[#151827] bg-[#f8d7d7] px-3 py-3 text-xs font-semibold text-[#151827]">
                      {requestError}
                    </div>
                  )}

                  <input
                    type="text"
                    placeholder={t('access_code.request_name_placeholder')}
                    value={requestForm.name}
                    onChange={(e) => setRequestForm({ ...requestForm, name: e.target.value })}
                    className={companyInputClass}
                    required
                  />
                  <input
                    type="email"
                    placeholder={t('access_code.request_email_placeholder')}
                    value={requestForm.email}
                    onChange={(e) => setRequestForm({ ...requestForm, email: e.target.value })}
                    className={companyInputClass}
                    required
                  />
                  <input
                    type="text"
                    placeholder={t('access_code.request_company_placeholder')}
                    value={requestForm.company_name}
                    onChange={(e) => setRequestForm({ ...requestForm, company_name: e.target.value })}
                    className={companyInputClass}
                    required
                  />
                  <input
                    type="text"
                    placeholder={t('access_code.request_phone_placeholder')}
                    value={requestForm.phone}
                    onChange={(e) => setRequestForm({ ...requestForm, phone: e.target.value })}
                    className={companyInputClass}
                  />
                  <textarea
                    rows={3}
                    placeholder={t('access_code.request_message_placeholder')}
                    value={requestForm.message}
                    onChange={(e) => setRequestForm({ ...requestForm, message: e.target.value })}
                    className={`${companyInputClass} resize-none`}
                  />

                  <div className="grid gap-3 sm:grid-cols-2">
                    <button
                      type="button"
                      onClick={() => setShowRequestForm(false)}
                      className={companySecondaryButtonClass}
                      style={{ boxShadow: '8px 8px 0 #151827' }}
                    >
                      {t('common.cancel')}
                    </button>
                    <button
                      type="submit"
                      disabled={requestBusy}
                      className={`w-full text-sm uppercase tracking-[0.12em] ${requestBusy ? 'cursor-not-allowed rounded-lg bg-gray-300 py-3 font-black text-[#151827]/60' : primaryButtonClass}`}
                      style={!requestBusy ? { boxShadow: '8px 8px 0 #151827' } : undefined}
                    >
                      {requestBusy ? t('auth.sending') : t('access_code.submit_request')}
                    </button>
                  </div>
                </form>
              )}
            </div>
          )}

          {isCandidate && (
            <div className="mt-8 border-t border-gray-200 pt-6 text-center">
              <p className="text-xs text-gray-500">
                {t('access_code.candidate_help_prefix')}{' '}
                <a href="mailto:info@betaaldsolliciteren.nl" className="font-semibold text-[#713d8d] hover:underline">
                  info@betaaldsolliciteren.nl
                </a>
              </p>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
